import { createCanvas, get2D } from '../render/canvasUtil';
import { computeScaledSize } from './imageWorkerProtocol';

/** 图像加载与预处理管线（主线程降级路径在此；Worker 路径见 imageWorkerClient）。 */

export interface PreparedImage {
  canvas: HTMLCanvasElement | OffscreenCanvas;
  width: number;
  height: number;
}

/** 从 URL 加载图片（支持跨域，用于示例图）。失败时 reject（生产级：明确报错）。 */
export function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`图片加载失败：${url}`));
    img.src = url;
  });
}

/** 从本地文件加载图片（拖拽/选文件）。内部释放 object URL。 */
export async function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  if (!file.type.startsWith('image/')) {
    throw new Error(`不支持的文件类型：${file.type || '未知'}（仅接受图片）`);
  }
  const url = URL.createObjectURL(file);
  try {
    return await loadImageFromUrl(url);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/** 主线程降级：把 ImageBitmap 等比缩放绘制到 HTMLCanvasElement。 */
export function drawBitmapToScaledCanvas(bitmap: ImageBitmap, maxDim: number): PreparedImage {
  const { width, height } = computeScaledSize(bitmap.width, bitmap.height, maxDim);
  const canvas = createCanvas(width, height);
  const ctx = get2D(canvas);
  ctx.drawImage(bitmap, 0, 0, width, height);
  if (typeof bitmap.close === 'function') bitmap.close();
  return { canvas, width, height };
}

/** 程序生成测试图（渐变 + 网格 + 编号），供无外部图时验证切割渲染。 */
export function makeTestImage(w: number, h: number, cols: number, rows: number): HTMLCanvasElement {
  const canvas = createCanvas(w, h);
  const ctx = get2D(canvas);

  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, '#f6d365');
  g.addColorStop(1, '#fda085');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  ctx.strokeStyle = 'rgba(255,255,255,0.6)';
  ctx.lineWidth = 2;
  for (let r = 0; r <= rows; r++) {
    ctx.beginPath();
    ctx.moveTo(0, (r * h) / rows);
    ctx.lineTo(w, (r * h) / rows);
    ctx.stroke();
  }
  for (let c = 0; c <= cols; c++) {
    ctx.beginPath();
    ctx.moveTo((c * w) / cols, 0);
    ctx.lineTo((c * w) / cols, h);
    ctx.stroke();
  }

  ctx.fillStyle = '#5a2a0a';
  ctx.font = `bold ${Math.round(Math.min(w / cols, h / rows) * 0.3)}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  let id = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      ctx.fillText(String(id++), (c + 0.5) * (w / cols), (r + 0.5) * (h / rows));
    }
  }
  return canvas;
}
